import { useEffect, useState, type FormEvent } from 'react'

import {
  api,
  ErrorApi,
  type EntradaIndustrial,
  type Espera,
  type FuenteConsumo,
  type OpcionesIndustrial,
  type OrigenGuardado,
  type ResultadoCotizacion as Resultado,
} from '../api'
import { leerNumero } from '../formato'
import { BarraGuardar } from './BarraGuardar'
import { CamposTarifa, deLaTarifa, type TextoTarifa } from './CamposTarifa'
import { DescuentoCliente } from './DescuentoCliente'
import { leerEspera, OrigenConsumo, SupuestosEspera, type TextoEspera } from './OrigenConsumo'
import { PartidasManuales, type FilaPartida } from './PartidasManuales'
import { ResultadoCotizacion } from './ResultadoCotizacion'

const TENSION: Record<string, string> = { '220': '220 V trifásico', '400': '230/400 V trifásico', '480': '480 V trifásico' }

interface Props {
  clienteId: string
  base?: EntradaIndustrial & OrigenGuardado
  alGuardar: (cotizacionId: string) => void
  alCancelar: () => void
}

const texto = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v))

export function NuevaCotizacionIndustrial({ clienteId, base, alGuardar, alCancelar }: Props) {
  const [opciones, setOpciones] = useState<OpcionesIndustrial | null>(null)
  const [consumo, setConsumo] = useState(texto(base?.consumo_kwh_mes))
  const [demanda, setDemanda] = useState(texto(base?.demanda_kw))
  const [zona, setZona] = useState(base?.zona ?? 'guayaquil')
  const [tension, setTension] = useState(base?.tension ?? '480')
  const [estructura, setEstructura] = useState(base?.estructura ?? 'techo')
  const [objetivo, setObjetivo] = useState(texto(base?.potencia_objetivo_kwp))
  const [control, setControl] = useState(base?.control_exportacion ?? false)
  const [tarifa, setTarifa] = useState<TextoTarifa | null>(
    base ? { codigo: base.tarifa_codigo, energia: texto(base.tarifa.energia_usd_kwh), demanda: texto(base.tarifa.demanda_usd_kw_mes), comercializacion: texto(base.tarifa.comercializacion_usd_mes) } : null,
  )
  const [evitada, setEvitada] = useState(texto(base?.kw_demanda_evitada ?? 0))
  const [partidas, setPartidas] = useState<FilaPartida[]>(
    (base?.partidas ?? []).map((p) => ({ descripcion: p.descripcion, costo: String(p.costo), iva: p.iva_pct === 0 ? '0' : '0.15', margen: p.margen == null ? '' : String(p.margen * 100) })),
  )
  const [descuento, setDescuento] = useState(base ? String(base.descuento_pct * 100) : '0')
  const [tasa, setTasa] = useState(base?.tasa_descuento ? String(base.tasa_descuento * 100) : '')
  const [nota, setNota] = useState('')
  const [fuente, setFuente] = useState<FuenteConsumo>(base?.origen_consumo?.fuente ?? 'manual')
  const [espera, setEspera] = useState<TextoEspera>({
    tarifa: String((base?.espera?.subida_anual_tarifa ?? 0) * 100),
    precio: String((base?.espera?.subida_anual_precio ?? 0) * 100),
  })

  const [calculada, setCalculada] = useState<{ entrada: EntradaIndustrial; espera: Espera; resultado: Resultado } | null>(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState<'' | 'calculando' | 'guardando'>('')

  useEffect(() => {
    api
      .opcionesIndustrial()
      .then((o) => {
        setOpciones(o)
        const porDefecto = o.tarifas.find((t) => t.codigo === o.tarifa_por_defecto) ?? o.tarifas[0]
        setTarifa((actual) => actual ?? (porDefecto ? deLaTarifa(porDefecto) : null))
      })
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.'))
  }, [])

  // Cualquier cambio invalida el resultado: guardar numeros de otra entrada seria
  // guardar una cotizacion que nadie pidio.
  const cambia = <T,>(fijar: (v: T) => void) => (v: T) => {
    fijar(v)
    setCalculada(null)
  }

  const armarEntrada = (): EntradaIndustrial | string => {
    const c = leerNumero(consumo)
    const dem = leerNumero(demanda)
    const obj = leerNumero(objetivo)
    const ev = leerNumero(evitada) ?? 0
    const d = leerNumero(descuento) ?? 0
    const s = leerNumero(tasa)
    if (c === null || !(c > 0)) return 'Escribe el consumo mensual en kWh.'
    if (dem === null || !(dem > 0)) return 'Escribe la demanda máxima del cliente en kW (sale de su planilla).'
    if (Number.isNaN(obj) || (obj !== null && !(obj > 0))) return 'La potencia objetivo va en kWp, por ejemplo 150.'
    if (Number.isNaN(ev) || ev < 0) return 'Los kW de demanda evitada no son un número válido.'
    if (Number.isNaN(d) || d < 0) return 'El descuento no es un número válido.'
    if (opciones && d / 100 > opciones.descuento_maximo_pct) return `El descuento máximo permitido es ${opciones.descuento_maximo_pct * 100} %.`
    if (Number.isNaN(s) || (s !== null && !(s > 0 && s <= 50))) return 'La tasa de descuento va en %, por ejemplo 6,45.'
    if (!tarifa) return 'Todavía no se cargaron las tarifas del pliego.'
    const [te, td, tc] = [leerNumero(tarifa.energia), leerNumero(tarifa.demanda), leerNumero(tarifa.comercializacion)]
    if (te === null || !(te > 0 && te <= 2)) return 'El precio del kWh va en USD, por ejemplo 0,118.'
    if (td === null || Number.isNaN(td) || td < 0 || tc === null || Number.isNaN(tc) || tc < 0) return 'Revisa el cargo de demanda y el de comercialización.'

    const filas = []
    for (const f of partidas) {
      const costo = leerNumero(f.costo)
      const margen = leerNumero(f.margen)
      const nombre = f.descripcion.trim() || 'sin descripción'
      if (!f.descripcion.trim()) return 'Hay una partida sin descripción.'
      if (costo === null || !(costo > 0)) return `Escribe el costo de «${nombre}» o quítala.`
      if (Number.isNaN(margen) || (margen !== null && margen < 0)) return `El margen de «${nombre}» no es válido.`
      filas.push({ descripcion: f.descripcion.trim(), costo, iva_pct: f.iva === '0' ? (0 as const) : (0.15 as const), margen: margen === null ? null : margen / 100 })
    }
    return {
      consumo_kwh_mes: c,
      demanda_kw: dem,
      zona,
      tension,
      estructura,
      tarifa_codigo: tarifa.codigo,
      tarifa: { energia_usd_kwh: te, demanda_usd_kw_mes: td, comercializacion_usd_mes: tc },
      kw_demanda_evitada: ev,
      potencia_objetivo_kwp: obj,
      control_exportacion: control,
      partidas: filas,
      descuento_pct: d / 100,
      tasa_descuento: s === null ? null : s / 100,
    }
  }

  const calcular = async (e: FormEvent) => {
    e.preventDefault()
    const entrada = armarEntrada()
    const supuestos = leerEspera(espera)
    if (typeof entrada === 'string' || typeof supuestos === 'string') {
      setError(typeof entrada === 'string' ? entrada : (supuestos as string))
      return
    }
    setError('')
    setOcupado('calculando')
    try {
      const { resultado } = await api.calcular(entrada, 'industrial', { clienteId, fuente, espera: supuestos })
      // Con proyeccion, consumo y demanda los pone el servidor: se muestran los que uso.
      const usado = resultado.consumo_usado
      if (usado) {
        setConsumo(String(usado.kwh_mes))
        if (usado.demanda_kw !== null) setDemanda(String(usado.demanda_kw))
      }
      setCalculada({ entrada, espera: supuestos, resultado })
    } catch (err) {
      setCalculada(null)
      setError(err instanceof ErrorApi ? err.message : 'No se pudo calcular.')
    } finally {
      setOcupado('')
    }
  }

  const guardar = async () => {
    if (!calculada) return
    setOcupado('guardando')
    try {
      const { cotizacion } = await api.guardarCotizacion(clienteId, calculada.entrada, nota, 'industrial', { fuente, espera: calculada.espera })
      alGuardar(cotizacion.id)
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
      setOcupado('')
    }
  }

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>{base ? 'Nueva versión industrial' : 'Nueva cotización industrial'}</h1>
          <p className="suave">Trifásica, sin tope de potencia, con tarifa de demanda y partidas manuales. Nada se envía al cliente.</p>
        </div>
        <button className="secundario" onClick={alCancelar}>
          Cancelar
        </button>
      </div>

      <form className="tarjeta formulario-cotizacion" onSubmit={calcular}>
        <OrigenConsumo
          clienteId={clienteId}
          industrial
          fuente={fuente}
          alCambiarFuente={cambia(setFuente)}
          alProyectar={(kwh, kw) => {
            cambia(setConsumo)(String(kwh))
            if (kw !== null) setDemanda(String(kw))
          }}
        />
        <label>
          Consumo mensual (kWh)
          <input value={consumo} onChange={(e) => cambia(setConsumo)(e.target.value)} inputMode="decimal" required readOnly={fuente === 'proyeccion'} />
        </label>
        <label>
          Demanda máxima (kW)
          <input value={demanda} onChange={(e) => cambia(setDemanda)(e.target.value)} inputMode="decimal" required readOnly={fuente === 'proyeccion'} />
          {fuente === 'proyeccion' && <small className="ayuda">Proyectados. Para escribirlos, elige «Escrito a mano».</small>}
        </label>
        <label>
          Tensión de conexión
          <select value={tension} onChange={(e) => cambia(setTension)(e.target.value)}>
            {(opciones?.tensiones ?? [tension]).map((t) => (
              <option key={t} value={t}>
                {TENSION[t] ?? `${t} V`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Zona
          <select value={zona} onChange={(e) => cambia(setZona)(e.target.value)}>
            {(opciones?.zonas ?? [zona]).map((z) => (
              <option key={z} value={z}>
                {z.charAt(0).toUpperCase() + z.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Estructura
          <select value={estructura} onChange={(e) => cambia(setEstructura)(e.target.value)}>
            <option value="techo">Techo de lámina</option>
            <option value="losa">Losa</option>
          </select>
        </label>
        <label>
          Potencia objetivo (kWp)
          <input value={objetivo} onChange={(e) => cambia(setObjetivo)(e.target.value)} inputMode="decimal" placeholder="Vacío = cubrir el consumo" />
          <small className="ayuda">Si el techo no da para todo, fíjala aquí.</small>
        </label>
        <label>
          Demanda evitada (kW)
          <input value={evitada} onChange={(e) => cambia(setEvitada)(e.target.value)} inputMode="decimal" />
          <small className="ayuda">Solo si el pico del cliente cae de día. 0 = no se cuenta ahorro de demanda.</small>
        </label>
        <label>
          Tasa de descuento (%)
          <input value={tasa} onChange={(e) => cambia(setTasa)(e.target.value)} inputMode="decimal" placeholder={opciones ? `${(opciones.tasa_descuento_cliente * 100).toFixed(2)} por defecto` : ''} />
        </label>
        <label className="casilla">
          <input type="checkbox" checked={control} onChange={(e) => cambia(setControl)(e.target.checked)} />
          Control de exportación cero (SEC1000)
        </label>

        {opciones && tarifa && <CamposTarifa tarifas={opciones.tarifas} valor={tarifa} alCambiar={cambia(setTarifa)} />}
        <PartidasManuales filas={partidas} alCambiar={cambia(setPartidas)} margenPorDefecto={opciones?.margen_por_defecto} />
        <DescuentoCliente valor={descuento} alCambiar={cambia(setDescuento)} maximo={opciones?.descuento_maximo_pct} />
        <SupuestosEspera valor={espera} alCambiar={cambia(setEspera)} />

        <div className="accion-formulario">
          <button className="primario" disabled={ocupado !== '' || !opciones}>
            {ocupado === 'calculando' ? 'Calculando…' : calculada ? 'Recalcular' : 'Calcular'}
          </button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}

      {calculada && (
        <>
          <ResultadoCotizacion resultado={calculada.resultado} />
          <BarraGuardar nota={nota} alCambiarNota={setNota} alGuardar={guardar} guardando={ocupado === 'guardando'} deshabilitado={ocupado !== ''} />
        </>
      )}
    </section>
  )
}
